import React from "react";
import {
  ArrowSquareOutIcon,
  LinkedinLogoIcon,
  GithubLogoIcon,
  XLogoIcon,
  SparkleIcon,
  DownloadIcon,
} from "@phosphor-icons/react";

import "./assets/css/general.css";
import profilePicture from "./assets/img/profilePicture2.webp";

const invoiceStackUrl = "https://invoice-stack.com";
const email = "hi@fernandoflorez.dev";
const cvUrl = "https://www.icloud.com/iclouddrive/05fTxH_YoRGeBEdVm527R4YVw"; // Add the hosted CV.pdf URL here.

const experiences = [
  {
    period: "2023 — Present",
    role: "Native App & AI Automation Developer",
    company: "Freelance",
    highlights: [
      "Build native iOS applications with Swift, SwiftUI, and UIKit, shaped around real user needs.",
      "Create AI-powered automations that simplify repetitive workflows and connect existing tools.",
    ],
  },
  {
    period: "2023 — 2025",
    role: "Senior iOS Developer",
    company: "Koombea",
    highlights: [
      "Built and shipped mobile experiences from the ground up with SwiftUI and UIKit.",
      "Partnered with product and design teams to turn requirements into polished, maintainable features.",
    ],
  },
  {
    period: "2022 — 2023",
    role: "Mid-Senior iOS Developer",
    company: "Empowerment Labs",
    highlights: [
      "Delivered and supported features for a product focused on people’s financial health.",
      "Worked closely with product, UX, and engineering to make thoughtful product decisions.",
    ],
  },
  {
    period: "2021 — 2022",
    role: "Mid-Senior iOS Developer",
    company: "Matter Supply Co.",
    highlights: [
      "Developed new product features alongside a multidisciplinary UI, UX, and product team.",
      "Contributed technical perspective to a product built for Fannie Mae.",
    ],
  },
  {
    period: "2020 — 2021",
    role: "iOS Developer",
    company: "Plankk",
    highlights: [
      "Developed iOS projects across new and existing fitness products.",
      "Collaborated across frontend, backend, and mobile teams in a multiplatform environment.",
    ],
  },
];

const ArrowIcon = () => <ArrowSquareOutIcon size={16} />;

function App() {
  return (
    <div className="site-shell">
      <header className="site-header">
        <a
          className="wordmark"
          href="#top"
          aria-label="Fernando Florez — back to top"
        >
          FF<span className="wordmark-dot">.</span>
        </a>

        <nav className="site-nav" aria-label="Main navigation">
          <a href="#work">Work</a>
          <a href="#experience">Experience</a>
          <a href="#about">About</a>
          <a href="#contact">Contact</a>
        </nav>

        <a
          className="button button-small header-cta"
          href={invoiceStackUrl}
          target="_blank"
          rel="noreferrer"
        >
          View InvoiceStack <ArrowIcon />
        </a>
      </header>

      <main id="top">
        <section className="hero section-frame" aria-labelledby="hero-title">
          <div className="hero-copy">
            <p className="eyebrow">
              <span className="status-dot" /> Available for thoughtful product
              work
            </p>
            <h1 id="hero-title">I build software people enjoy using.</h1>
            <p className="hero-intro">
              I’m Fernando Florez, a software engineer who turns complex
              workflows into simple, reliable digital products.
            </p>
            <div className="hero-actions">
              <a className="button" href="#work">
                See featured work
              </a>
              <a className="text-link" href={`mailto:${email}`}>
                Let’s talk
              </a>
            </div>
          </div>

          <div className="hero-art" aria-label="InvoiceStack product preview">
            <div className="hero-orbit hero-orbit-one" />
            <div className="hero-orbit hero-orbit-two" />
            <div className="product-window">
              <div className="window-topbar">
                <span /> <span /> <span />
                <p>invoice-stack.com</p>
              </div>
              <div className="product-window-content">
                <div className="product-brand-row">
                  <span className="product-mark">IS</span>
                  <span>InvoiceStack</span>
                </div>
                <div className="invoice-card">
                  <div className="invoice-card-heading">
                    <span>Invoice</span>
                    <strong>Ready</strong>
                  </div>
                  <div className="invoice-lines">
                    <i />
                    <i />
                    <i />
                  </div>
                  <div className="invoice-total">
                    <span>Total</span>
                    <b>••••</b>
                  </div>
                </div>
                <div className="product-chips">
                  <span>Simple</span>
                  <span>Reliable</span>
                  <span>Focused</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          className="featured-work section-frame"
          id="work"
          aria-labelledby="work-title"
        >
          <div className="section-heading">
            <p className="eyebrow">01 / Featured product</p>
            <h2 id="work-title">
              Building a clearer way to work with invoices.
            </h2>
          </div>

          <article className="project-feature">
            <div className="project-visual">
              <div className="project-grid" />
              <div className="project-visual-header">
                <span className="product-mark">IS</span>
                <span>InvoiceStack</span>
              </div>
              <div className="project-visual-panel">
                <p>InvoiceStack</p>
                <div className="visual-rule" />
                <div className="visual-row">
                  <span>Clear workflow</span>
                  <i />
                </div>
                <div className="visual-row">
                  <span>Thoughtful details</span>
                  <i />
                </div>
                <div className="visual-row">
                  <span>Product-led build</span>
                  <i />
                </div>
              </div>
              <p className="visual-caption">Live product</p>
            </div>

            <div className="project-content">
              <p className="project-kicker">InvoiceStack</p>
              <h3>A product built around a workflow, not a tool list.</h3>
              <p>
                InvoiceStack is my current live product. I’m shaping the product
                experience end-to-end—from defining the workflow to building the
                interface and the systems behind it.
              </p>
              <dl className="project-details">
                <div>
                  <dt>Focus</dt>
                  <dd>Product, engineering, and experience</dd>
                </div>
                <div>
                  <dt>Role</dt>
                  <dd>Product &amp; software engineer</dd>
                </div>
                <div>
                  <dt>Status</dt>
                  <dd>Live and actively evolving</dd>
                </div>
              </dl>
              <a
                className="button button-dark"
                href={invoiceStackUrl}
                target="_blank"
                rel="noreferrer"
              >
                Visit InvoiceStack <ArrowIcon />
              </a>
            </div>
          </article>
        </section>

        <section
          className="experience section-frame"
          id="experience"
          aria-labelledby="experience-title"
        >
          <div className="section-heading section-heading-inline">
            <div>
              <p className="eyebrow">02 / Experience</p>
              <h2 id="experience-title">
                Teams, products, and the work behind the release.
              </h2>
            </div>
            <a
              className="text-link"
              href="https://www.linkedin.com/in/fernando-florez/"
              target="_blank"
              rel="noreferrer"
            >
              More on LinkedIn
            </a>
          </div>

          <div className="experience-list">
            {experiences.map((experience) => (
              <article
                className="experience-item"
                key={`${experience.company}-${experience.period}`}
              >
                <p className="experience-period">{experience.period}</p>
                <div>
                  <h3>{experience.role}</h3>
                  <p className="experience-company">{experience.company}</p>
                </div>
                <ul>
                  {experience.highlights.map((highlight) => (
                    <li key={highlight}>{highlight}</li>
                  ))}
                </ul>
                {experience.company === "Freelance" && (
                  <a className="experience-cta" href={`mailto:${email}`}>
                    Contact me
                  </a>
                )}
              </article>
            ))}
          </div>
        </section>

        <section
          className="about section-frame"
          id="about"
          aria-labelledby="about-title"
        >
          <div className="about-image-wrap">
            <img src={profilePicture} alt="Fernando Florez" />
            <p>Based in Colombia, working globally.</p>
          </div>
          <div className="about-copy">
            <p className="eyebrow">03 / About</p>
            <h2 id="about-title">
              Good products happen when the details have a reason.
            </h2>
            <p>
              I care about the space between an idea and a product people can
              actually rely on. That means asking better questions,
              collaborating closely, and making technical choices that serve the
              experience—not the other way around.
            </p>
            <p>
              My work spans product thinking, frontend and backend development,
              and mobile when the problem calls for it.
            </p>
            <a
              className="button button-dark cv-link"
              href={cvUrl || undefined}
              aria-disabled={!cvUrl}
              title={!cvUrl ? "Add the CV URL in src/App.js" : undefined}
              target={cvUrl ? "_blank" : undefined}
              rel={cvUrl ? "noreferrer" : undefined}
            >
              <DownloadIcon size={18} /> CV.pdf
            </a>
          </div>
        </section>

        <section
          className="contact section-frame"
          id="contact"
          aria-labelledby="contact-title"
        >
          <p className="eyebrow">04 / Contact</p>
          <h2 id="contact-title">Have a product worth building?</h2>
          <a className="contact-email" href={`mailto:${email}`}>
            {email}
          </a>
          <div className="contact-links">
            <a
              href="https://www.linkedin.com/in/fernando-florez/"
              target="_blank"
              rel="noreferrer"
            >
              <LinkedinLogoIcon size={16} /> fernando-florez
            </a>
            <a
              href="https://github.com/unferna"
              target="_blank"
              rel="noreferrer"
            >
              <GithubLogoIcon size={16} />
              unferna
            </a>
            <a href="https://x.com/unfernaonx" target="_blank" rel="noreferrer">
              <XLogoIcon size={16} /> unfernaonx
            </a>
            <a
              href="https://ishipthis.com/unferna"
              target="_blank"
              rel="noreferrer"
            >
              I ship this!
            </a>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <p>© {new Date().getFullYear()} Fernando Florez</p>
        <div className="links">
          <a href="/llms.txt">
            <SparkleIcon size={16} style={{ verticalAlign: "text-top" }} />{" "}
            llms.txt
          </a>
          <a
            className="cv-link"
            href={cvUrl || undefined}
            aria-disabled={!cvUrl}
            title={!cvUrl ? "Add the CV URL in src/App.js" : undefined}
            target={cvUrl ? "_blank" : undefined}
            rel={cvUrl ? "noreferrer" : undefined}
          >
            <DownloadIcon size={16} style={{ verticalAlign: "text-top" }} />{" "}
            CV.pdf
          </a>
          <a href="#top">Back to top ↑</a>
        </div>
      </footer>
    </div>
  );
}

export default App;
